import { cn } from '@/lib/utils';
import type {
  AgentRunDisplay,
  AgentRunDisplayStage,
  AgentRunDisplayStatus,
  AgentRunDisplayToolCall,
  AgentRunStageStatus,
} from '@/lib/agentRunDisplay';

type AgentRunTimelineProps = {
  stages: AgentRunDisplayStage[];
  className?: string;
};

type AgentRunCardProps = {
  run: AgentRunDisplay | null;
  className?: string;
  showIdentifiers?: boolean;
};

const STATUS_TONE_CLASSES: Record<AgentRunDisplayStatus, string> = {
  queued: 'border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300',
  running: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
  needs_attention: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  completed: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  failed: 'border-destructive/40 bg-destructive/10 text-destructive',
};

const DOT_CLASSES: Record<AgentRunStageStatus, string> = {
  pending: 'border-border bg-muted',
  active: 'border-blue-500 bg-blue-500 shadow-[0_0_0_4px_rgba(59,130,246,0.16)]',
  completed: 'border-emerald-500 bg-emerald-500',
  failed: 'border-destructive bg-destructive',
};

const CONNECTOR_CLASSES: Record<AgentRunStageStatus, string> = {
  pending: 'bg-border/60',
  active: 'bg-blue-300 dark:bg-blue-700',
  completed: 'bg-emerald-300 dark:bg-emerald-700',
  failed: 'bg-destructive/50',
};

function truncateIdentifier(value: string): string {
  return value.length > 14 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
}

function ToolCallRow({ call }: { call: AgentRunDisplayToolCall }) {
  const failed = call.status === 'failed';
  const completed = call.status === 'completed';
  const pending = call.status === 'pending';

  return (
    <li className="flex min-w-0 items-start gap-2 text-xs text-muted-foreground">
      <span
        className={cn(
          'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
          failed && 'bg-destructive',
          completed && 'bg-emerald-500',
          pending && 'bg-muted-foreground/50',
          !failed && !completed && !pending && 'bg-blue-500 animate-pulse motion-reduce:animate-none',
        )}
        aria-hidden="true"
      />
      <span className="min-w-0">
        <span className="font-medium text-foreground">{call.name}</span>
        {call.summary ? <span className="ml-1 text-muted-foreground">{call.summary}</span> : null}
      </span>
    </li>
  );
}

export function AgentRunTimeline({ stages, className }: AgentRunTimelineProps) {
  if (!stages.length) return null;

  return (
    <ol
      className={cn('flex items-start gap-1 overflow-x-auto pb-1', className)}
      aria-label="Agent run progress"
    >
      {stages.map((stage, index) => {
        const isLast = index === stages.length - 1;
        return (
          <li key={`${stage.id}-${index}`} className="flex shrink-0 items-start gap-1">
            <div className="flex w-[72px] flex-col items-center gap-1 text-center">
              <span
                className={cn('h-2.5 w-2.5 rounded-full border', DOT_CLASSES[stage.status])}
                aria-label={`${stage.label}: ${stage.status}`}
              />
              <span
                className={cn(
                  'text-[10px] leading-tight text-muted-foreground',
                  stage.status === 'active' && 'font-medium text-blue-700 dark:text-blue-300',
                  stage.status === 'completed' && 'text-emerald-700 dark:text-emerald-300',
                  stage.status === 'failed' && 'text-destructive',
                )}
              >
                {stage.label}
              </span>
            </div>
            {!isLast ? (
              <span
                className={cn('mt-1 h-px w-5 shrink-0', CONNECTOR_CLASSES[stage.status])}
                aria-hidden="true"
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export function AgentRunCard({ run, className, showIdentifiers = false }: AgentRunCardProps) {
  if (!run) return null;

  return (
    <section
      className={cn(
        'mt-3 rounded-xl border border-border/50 bg-background/65 px-4 py-3 shadow-sm',
        'text-foreground',
        className,
      )}
      role="status"
      aria-live={run.status === 'running' || run.status === 'queued' ? 'polite' : 'off'}
      aria-label={`${run.title}: ${run.statusLabel}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-5 text-foreground">{run.title}</p>
          {run.summary ? (
            <p className="mt-0.5 line-clamp-2 text-xs leading-5 text-muted-foreground">
              {run.summary}
            </p>
          ) : null}
        </div>
        <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium', STATUS_TONE_CLASSES[run.status])}>
          {run.statusLabel}
        </span>
      </div>

      <AgentRunTimeline stages={run.stages} className="mt-3" />

      {run.toolCalls.length ? (
        <ul className="mt-2 space-y-1">
          {run.toolCalls.map((call) => (
            <ToolCallRow key={`${call.id}-${call.name}`} call={call} />
          ))}
        </ul>
      ) : null}

      {showIdentifiers && (run.runId || run.threadId) ? (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
          {run.runId ? <span>Run {truncateIdentifier(run.runId)}</span> : null}
          {run.threadId ? <span>Thread {truncateIdentifier(run.threadId)}</span> : null}
        </div>
      ) : null}
    </section>
  );
}
